import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { ModalService } from '../../utils/services/modal.service';
import { ModalComponent } from '../../ui/modal/modal.component';
import {
  PaymentReason,
  Transaction,
  TransactionStatus,
  TransactionType,
} from '../../schemas/transaction';
import { BusinessService } from '../../services/business.service';
import { AccountTypeCode, PaymentAccount } from '../../schemas/payment_account';
import { FormsModule } from '@angular/forms';
import { BossServicesService } from '../../services/boss-services.service';
import { TranslocoModule } from '@jsverse/transloco';
import { Locale } from '../../schemas/locale';
import { QrCodeComponent } from '../../ui/qr-code/qr-code.component';
import { TransactionService } from '../../services/transaction.service';
import { Business } from '../../schemas/business';
import { UserService } from '../../services/user.service';
import { PaymentProvider } from '../../schemas/payment_provider';
import { Router } from '@angular/router';
import { Platform } from '@angular/cdk/platform';

@Component({
  selector: 'qwiko-new-payment',
  imports: [
    CommonModule,
    ModalComponent,
    FormsModule,
    TranslocoModule,
    QrCodeComponent,
  ],
  templateUrl: './new-payment.component.html',
  styleUrl: './new-payment.component.scss',
})
export class NewPaymentComponent implements OnDestroy {
  modalService = inject(ModalService);
  transactionService = inject(TransactionService);
  businessService = inject(BusinessService);
  userService = inject(UserService);
  services = inject(BossServicesService);
  transactionStatus = TransactionStatus;
  router = inject(Router);
  platform = inject(Platform);

  business = computed<Business | null>(() => {
    return this.businessService.business();
  });

  amountInputRef = viewChild<ElementRef<HTMLInputElement>>('amount_number');
  amountInputRefEffect = effect(() => {
    if (this.amountInputRef() && this.amountInputRef()!.nativeElement) {
      this.amountInputRef()!.nativeElement.focus();
    }
  });
  measureRef = viewChild<ElementRef<HTMLSpanElement>>('measure');

  businessPaymentAccounts = this.businessService.businessPaymentAccounts;
  country = computed<Locale>(() => {
    return this.businessService.business()?.country as Locale;
  });

  loading = model<boolean>(false);

  isNewPayment = this.services.isNewPayment;

  isNewPaymentEffect = effect(() => {
    if (this.isNewPayment()) {
      this.modalService.open('new-payment-service');
      this.formatAmount();
    }
  });

  // isCreatedPaymentEffect = effect(() => {
  //   console.log('this.services.isNewPayment():', this.services.isNewPayment());
  // });

  isAddAmount = signal<boolean>(false);
  isAddCustomer = signal<boolean>(false);
  isOffLineMode = signal<boolean>(false);
  isChoosePaymentAccount = signal<boolean>(false);
  isAddPaymentReason = signal<boolean>(false);
  isQRCode = signal<boolean>(false);
  isConfirmTransaction = signal<boolean>(false);
  hasFinalStatus = signal<boolean>(false);
  hasNotConfirmed = signal<boolean>(false);
  isDoNotAsk = signal<boolean>(false);

  transaction = model<Transaction | null>(null);
  transactionEffect = effect(() => {
    if (this.transaction()) {
      if (
        this.transaction()?.status === this.transactionStatus.CANCELED ||
        this.transaction()?.status === this.transactionStatus.CONFIRMED
      ) {
        this.resetModalStates();
        this.hasFinalStatus.set(true);
      }
    }
  });
  hasTransaction = input<boolean>(false);

  amount = model<string | null>('0');
  amountToSave = computed<number | null>(() => {
    if (this.amount() == null) {
      return 0;
    }
    const amount = this.amount()!.split(',').join('');
    return parseInt(amount);
  });

  customerNumber = model<string | null>(null);
  localAccount = signal<PaymentAccount | null>(null);
  formattedLocalAccountNumber = signal<string | null>(null);
  localAccountEffect = effect(() => {
    this.customerNumber();
    if (!!this.localAccount()) {
      const customerNumber = this.localAccount()?.number;
      const code = (this.localAccount()?.country as Locale)?.phone_code || '';
      const formattedNumber = customerNumber?.replace(code, '') || '';
      if (formattedNumber) {
        this.customerNumber.set(formattedNumber);
        this.formattedLocalAccountNumber.set(formattedNumber);
      }
    }
  });

  customerNumberFormatted = computed<string>(() => {
    const code = this.country()?.phone_code;
    if (!this.customerNumber()) {
      return '';
    }
    return `${code}${this.customerNumber()}`;
  });

  customerNumberDisplay = computed<string>(() => {
    const code = this.country()?.phone_code;
    const starts_with = this.country()?.starts_with;

    if (this.customerNumberFormatted().startsWith(code!) && starts_with) {
      const index = this.customerNumberFormatted().indexOf(starts_with!);

      if (index !== -1) {
        const sliceIndex = index + starts_with!.length;
        const remainder = this.customerNumberFormatted().slice(sliceIndex);

        return `${starts_with}${remainder}`;
      }
    }

    return this.customerNumberFormatted();
  });

  status = signal<TransactionStatus>(TransactionStatus.INITIATED);
  paymentAccount = model<PaymentAccount | null>(null);
  paymentReason = signal<PaymentReason | null>(null);

  // callTrigger = viewChild<HTMLAnchorElement>('callTrigger');
  ussdAmount = computed(() => {
    if (this.amountToSave() === null || this.amountToSave() === 0) {
      return 0;
    }

    // let number: string | number = this.amount()!;

    // number = number.replace(/[\s,]/g, '');
    // number = number.replace(/[^0-9,]/g, '');

    return this.amountToSave();
  });

  trigger = computed<string>(() => {
    let number = '0';
    number = this.paymentAccount()?.number_display || '0';

    const amount = this.ussdAmount()?.toString() || '0';

    const trigger = this.ussd()
      .replace(':number', number)
      .replace(':code', number)
      .replace(':amount', amount);

    return trigger;
  });

  ussd = computed<string>(() => {
    if (this.paymentAccount()) {
      const paymentProvider = this.paymentAccount()?.payment_provider;
      const ussds_to = paymentProvider?.ussds_to;

      const ussd = ussds_to?.find((ussd) => {
        const hasService = ussd.service?.code === 'SEND_MONEY';
        const hasFromAccountType = ussd.from_account_type?.code === 'MOMO';
        const hasToProvider = ussd.to_provider?.code === paymentProvider?.code;

        if (hasService && hasFromAccountType && hasToProvider) {
          return ussd;
        }

        return null;
      });

      // console.log('ussd', ussd);

      if (ussd) {
        return ussd.ussd_code!;
      }

      return '';
    }
    return '';
  });

  constructor() {
    const localAccount = localStorage.getItem('local_account');
    if (localAccount) {
      this.localAccount.set(JSON.parse(localAccount));
    }
  }

  addAmount() {
    this.resetModalStates();
    this.isAddAmount.set(true);
  }

  choosePaymentAccount() {
    this.resetModalStates();
    this.isChoosePaymentAccount.set(true);
  }

  changePaymentAccount() {
    this.resetModalStates();
    this.isChoosePaymentAccount.set(true);
    this.hasNotConfirmed.set(true);
  }

  transactionConfirmation() {
    this.resetModalStates();
    this.isConfirmTransaction.set(true);
    this.hasNotConfirmed.set(true);
  }

  cancelTransaction() {
    this.resetModalStates();
    this.status.set(TransactionStatus.CANCELED);
    this.updateTransaction();
  }

  async saveTransaction(event?: boolean) {
    this.loading.set(true);

    if (!this.hasNotConfirmed()) {
      // window.open(this.ussd(), '_self');
      // this.callTrigger()?.click();
      await this.openHashtagInfoModal();
      window.open(`tel://${this.trigger()}`, '_parent');
      // console.log('this.callTrigger()', `tel://${this.trigger()}`);

      if (!this.hasTransaction()) {
        const transaction: Transaction = {
          // id: crypto.randomUUID(),
          type: TransactionType.PAYMENT,
          status: TransactionStatus.INITIATED,
          amount: this.amountToSave()!.toString(),
          customer_number: this.customerNumberFormatted() || null,
          sender_status: TransactionStatus.INITIATED,
          // receiver: this.userService.user()?.id || null,
          receiver_account: this.paymentAccount()?.id || null,
          business_receiver: this.business()?.id,
          country: (this.business()?.country as Locale).id,
        };

        const transactionResponse =
          await this.transactionService.createTransaction(transaction);
        this.transaction.set(transactionResponse);
      } else {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }
      this.setLocalAccount();

      // console.log('transaction', this.transaction());
    }

    this.loading.set(false);

    if (event && this.hasNotConfirmed()) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
    }
    this.transactionConfirmation();
  }

  async openHashtagInfoModal() {
    if (this.platform.ANDROID) {
      this.modalService.open('hashtag-info-modal');
      await new Promise((resolve) => setTimeout(resolve, 2000));
      this.modalService.close('hashtag-info-modal');
    }
  }

  async confirmTransaction() {
    this.status.set(TransactionStatus.CONFIRMED);
    await this.updateTransaction();
  }

  setLocalAccount() {
    const paymentAccount: PaymentAccount = {
      number: this.customerNumberFormatted(),
      number_display: this.customerNumberDisplay(),
      label: (this.paymentAccount()?.payment_provider as PaymentProvider).name,
      type: this.paymentAccount()?.type,
      payment_provider: this.paymentAccount()?.payment_provider,
      country: this.business()?.country,
      business: this.business()?.id,
    };

    localStorage.setItem('local_account', JSON.stringify(paymentAccount));
  }

  async updateTransaction() {
    this.loading.set(true);

    const transaction: Transaction = {
      id: this.transaction()?.id,
      customer_number: this.customerNumberFormatted() || null,
      receiver_account: this.paymentAccount()?.id || null,
      sender_status: this.status(),
    };
    await this.transactionService.updateTransaction(transaction);
    this.loading.set(false);
    this.closeModal();
  }

  selectPaymentAccount(account: PaymentAccount) {
    this.paymentAccount.set(account);
    this.resetModalStates();
    if (this.isOffLineMode()) {
      if (this.hasNotConfirmed()) {
        this.isConfirmTransaction.set(true);
        this.hasNotConfirmed.set(false);
        return;
      }
      this.isQRCode.set(true);
      return;
    }

    if (this.hasNotConfirmed()) {
      this.isConfirmTransaction.set(true);
      this.hasNotConfirmed.set(false);
      return;
    }
    this.isNewPayment.set(true);
  }

  allAccounts() {
    this.paymentAccount.set(null);
    this.resetModalStates();
    if (this.isOffLineMode()) {
      if (this.hasNotConfirmed()) {
        this.isConfirmTransaction.set(true);
        this.hasNotConfirmed.set(false);
        return;
      }
      this.isOffLineMode.set(false);
      this.isQRCode.set(true);
      return;
    }

    if (this.hasNotConfirmed()) {
      this.isConfirmTransaction.set(true);
      this.hasNotConfirmed.set(false);
      return;
    }
    this.isNewPayment.set(true);
  }

  addPaymentReason() {
    this.resetModalStates();
    this.isAddPaymentReason.set(true);
  }

  selectPaymentReason(reason: PaymentReason) {
    this.paymentReason.set(reason);
    this.resetModalStates();
    this.isNewPayment.set(true);
  }

  openQRCode() {
    this.resetModalStates();
    this.isQRCode.set(true);
  }

  createTransaction() {
    this.closeModal();
  }

  customerNumberChange(event?: any) {
    if (this.customerNumber() === null || this.customerNumber() === '0') {
      return;
    }

    // const starts_with = this.country()?.starts_with;
    let number: string = this.customerNumber()?.split(' ').join('') || '';
    number = number.replace(/[^0-9]/g, '');
    const intNumber: number = parseInt(number);
    const numberString: string = intNumber.toString();

    if (isNaN(intNumber) || intNumber == 0) {
      this.customerNumber.set(null);
      return;
    }
    this.customerNumber.set(numberString);
    return;
  }

  formatCustomerNumberOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.customerNumber.set(pastedText ?? '');
    this.customerNumberChange();
  }

  formatAmount(event?: any) {
    // console.log('event:', event);
    // console.log('amount:', this.amount());
    if (event && event.key == 'Enter') {
      this.back();
    }

    if (this.amount() === null || this.amount() === '0') {
      return;
    }

    let number: string | number = this.amount()!;

    number = number.replace(/[\s,]/g, '');
    number = number.replace(/[^0-9,]/g, '');

    let numberInt = parseInt(number);

    // console.log('number:', number);
    // console.log('numberInt', numberInt);

    if (isNaN(numberInt) || numberInt == 0) {
      // console.log('is 0 or NaN', numberInt);
      this.amount.set(null);
      // console.log('amount after:', this.amount());
      return;
    }

    number = numberInt.toLocaleString('en-US');
    this.amount.set(number);

    return;
  }

  formatOnPaste(event: ClipboardEvent) {
    // console.log('paste event:', event);
    event.preventDefault();

    let pastedText = event.clipboardData?.getData('text');
    pastedText = pastedText?.split(' ')?.join('');

    this.amount.set(pastedText ?? '');
    this.formatAmount();
  }

  // transform(event: any) {
  //   if (this.measureRef() && this.amountInputRef()) {
  //     // Add extra padding to prevent text from touching the edges
  //     const width = Math.min(
  //       this.measureRef()!.nativeElement.offsetWidth + 10,
  //       this.amountInputRef()!.nativeElement.parentElement?.offsetWidth ||
  //         Infinity,
  //     );
  //     this.amountInputRef()!.nativeElement.style.width = `${width}px`;
  //   }
  // }

  async closeModal() {
    this.modalService.closeAll();
    this.amount.set(null);
    if (this.hasFinalStatus()) {
      await this.router.navigate(['/'], {
        queryParams: { b: this.business()?.code },
      });

      location.reload();
    }
    this.resetAllStates();
  }

  back(modal?: string) {
    if (this.isConfirmTransaction()) {
      this.resetModalStates();
      this.isQRCode.set(true);
      return;
    }

    if (this.isQRCode() && this.hasNotConfirmed()) {
      this.resetAllStates();
    }

    this.resetModalStates();
    // this.services.isNewPayment.set(false);
    this.isNewPayment.set(true);
    // if(this.isOffLineMode()) {
    //   this.isOffLineMode.set(false)
    // }
  }

  resetAllStates() {
    this.resetModalStates();
    this.paymentReason.set(null);
    this.isOffLineMode.set(false);
    this.hasNotConfirmed.set(false);
    this.transaction.set(null);
    this.status.set(TransactionStatus.INITIATED);
  }

  resetModalStates() {
    this.isAddAmount.set(false);
    this.isChoosePaymentAccount.set(false);
    this.isConfirmTransaction.set(false);
    this.isAddPaymentReason.set(false);
    this.isQRCode.set(false);
    // this.services.isNewPayment.set(false);
    this.isNewPayment.set(false);
    this.hasFinalStatus.set(false);
  }

  ngOnDestroy(): void {
    this.resetAllStates();
  }
}
function elseif() {
  throw new Error('Function not implemented.');
}
